import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AccountType } from '@/common/types'
import type { Account, User } from '@/common/types'
import { resolveActiveAccount } from './accountSelectionResolver'
import { APP_ROUTES } from '@/common/constants'
import { isAdviserRouteAllowed } from '@/common/navigation/accountRoutes'
import useRouteAccountEnforcement from './useRouteAccountEnforcement'
import useAccountStore from './accountStore'
import useAccountSwitcher from './useAccountSwitcher'
import { useAccountSwitchStateMachine } from './useAccountSwitchStateMachine'
import { useFetchAccounts } from './accountQueries'
import { useFetchUser } from '@/user/userQueries'
import { analytics } from '@/common/analytics/analytics'
import { getStoredUTMParameters } from '@/common/analytics/utm'
import { useIsStandalone } from '@/common/pwa/webauthn'

const ACCOUNT_FETCH_FAILURE_COOLDOWN_MS = 30_000
const ACCOUNT_SWITCH_GRACE_PERIOD_MS = 500

const isIgnorableQueryError = (error: unknown): boolean => {
    const candidate = error as { name?: string; code?: string; message?: string } | null
    if (!candidate) return false

    const code = candidate.code?.toLowerCase()
    const name = candidate.name?.toLowerCase()
    const message = candidate.message?.toLowerCase()

    return (
        code === 'err_canceled' ||
        name === 'cancelederror' ||
        name === 'aborterror' ||
        message?.includes('canceled') === true ||
        message?.includes('cancelled') === true ||
        message?.includes('aborted') === true
    )
}

const shouldDeferPrimaryAccountSettle = (args: {
    allowPrimaryAdviserAccount: boolean
    userAccounts: Account[] | undefined
    activeAccountId: string | null
    userAccountId: string | null
    userAccountType: AccountType | undefined
}) => {
    const { allowPrimaryAdviserAccount, userAccounts, activeAccountId, userAccountId, userAccountType } = args

    // Defer resolution when ALL of these are true:
    // 1. We're on an adviser route (user MAY use their own adviser account as active)
    // 2. The accounts query hasn't settled yet (userAccounts === undefined)
    // 3. The currently active account is the user's own account AND it's an adviser account
    //
    // Why: On adviser routes, we allow the adviser's own account to be the active account.
    // If the accounts query is still in-flight, resolveActiveAccount might incorrectly
    // switch away from the adviser account before the query confirms it's allowed.
    // We wait for the query to settle before re-resolving.
    return (
        allowPrimaryAdviserAccount &&
        userAccounts === undefined &&
        activeAccountId !== null &&
        activeAccountId === userAccountId &&
        userAccountType === AccountType.ADVISER
    )
}

type Props = {
    children: ReactNode
    user: User | null
}

export default function AccountProvider({ children, user }: Props) {
    const { error: userError, isPending: isUserPending } = useFetchUser(true)
    const { data: userAccounts } = useFetchAccounts(user?.userId ?? null, !!user)
    const derivedPrimaryAccount = useMemo(
        () => userAccounts?.find((a) => a.accountCategory === 'PRIMARY') ?? null,
        [userAccounts],
    )
    const installedPwa = useIsStandalone()
    const {
        activeAccountId,
        isAccountSwitching,
        accountSwitchTargetId,
        switchSessionToken,
        setAccounts,
        finishAccountSwitch,
        setPrimaryAccountId,
        setAccountsLoaded,
    } = useAccountStore()
    const switchAccount = useAccountSwitcher()
    const [failedAccountIds, setFailedAccountIds] = useState<Set<string>>(new Set())
    const cooldownTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
    const [isInGracePeriod, setIsInGracePeriod] = useState(false)
    const location = useLocation()

    const isApplicationCreateRoute = location.pathname === APP_ROUTES.APPLICATION_CREATE
    const isTermsAndConditionsUpdateRoute = location.pathname === APP_ROUTES.TERMS_AND_CONDITIONS_UPDATE

    const allowPrimaryAdviserAccount =
        isAdviserRouteAllowed({
            pathname: location.pathname,
            primaryAccount: derivedPrimaryAccount,
            activeAccountId,
        }) ||
        isApplicationCreateRoute ||
        isTermsAndConditionsUpdateRoute

    const shouldDeferUntilPrimaryAccountSettles = useMemo(
        () =>
            shouldDeferPrimaryAccountSettle({
                allowPrimaryAdviserAccount,
                userAccounts,
                activeAccountId,
                userAccountId: user?.accountId ?? null,
                userAccountType: user?.accountType,
            }),
        [allowPrimaryAdviserAccount, userAccounts, activeAccountId, user?.accountId, user?.accountType],
    )

    const nextActive = useMemo(() => {
        if (isAccountSwitching || shouldDeferUntilPrimaryAccountSettles) {
            return activeAccountId
        }

        // Grace period after successful switch: don't re-resolve immediately.
        // Gives the backend time to update account data after a manual selection.
        if (isInGracePeriod) {
            return activeAccountId
        }

        // Accounts haven't loaded yet (e.g. right after a hard refresh, before the
        // fetch resolves). Resolving against an empty accounts array would find the
        // persisted activeAccountId "missing" and fall through to null/primary,
        // wiping a valid selection. Hold the current selection until data arrives.
        if (userAccounts === undefined && activeAccountId !== null) {
            return activeAccountId
        }

        return resolveActiveAccount({
            currentActive: activeAccountId,
            userAccountId: user?.accountId ?? null,
            primaryAccount: derivedPrimaryAccount,
            accounts: userAccounts ?? [],
            failedAccountIds,
            allowPrimaryAdviserAccount,
        })
    }, [
        isAccountSwitching,
        shouldDeferUntilPrimaryAccountSettles,
        activeAccountId,
        user,
        derivedPrimaryAccount,
        userAccounts,
        failedAccountIds,
        allowPrimaryAdviserAccount,
        isInGracePeriod,
    ])

    const { shouldFinishSwitch: shouldFinishAccountSwitch } = useAccountSwitchStateMachine({
        isAccountSwitching,
        accountSwitchTargetId,
        activeAccountId,
        switchSessionToken,
        userAccounts,
        resolvedPrimaryAccount: derivedPrimaryAccount,
        isUserPending,
        userError,
    })

    useRouteAccountEnforcement({
        isAccountSwitching,
        activeAccountId,
        failedAccountIds,
        resolvedPrimaryAccount: derivedPrimaryAccount,
        switchAccount,
    })

    // ============================================================================
    // Effects: Ordering Contract
    // ============================================================================
    // These effects coordinate account resolution, switching, and error handling.
    // They rely on declaration order for correct behavior:
    //
    // 1. clearFailedAccount effect: Clear failed status when account is switched to
    // 2. handleAccountError effect: Mark account failed on error, set cooldown timer
    //    (must run AFTER clearFailedAccount so a just-failed account isn't immediately cleared)
    // 3. syncAccountsAndSwitch effect: Sync accounts to store and switch if needed
    // 4. finishAccountSwitch effect: Mark switch complete and enter grace period
    // 5. analyticsIdentify effect: Identify user with current account
    //
    // Changing this order can cause subtle race conditions. Do not reorder without
    // understanding the dependencies.
    // ============================================================================

    // Reset accounts state when user logs out
    useEffect(() => {
        if (isUserPending) return
        if (!user) {
            setPrimaryAccountId(null)
            setAccountsLoaded(false)
        }
    }, [user, setPrimaryAccountId, setAccountsLoaded, isUserPending])

    useEffect(() => {
        if (!user || !activeAccountId) return

        setFailedAccountIds((prev) => {
            if (!prev.has(activeAccountId)) return prev
            const next = new Set(prev)
            next.delete(activeAccountId)
            return next
        })
    }, [user, activeAccountId])

    useEffect(() => {
        const hasHardError = !!userError && !isIgnorableQueryError(userError)
        const shouldFail = hasHardError && !!activeAccountId && !isAccountSwitching

        if (!shouldFail) return

        const accountId = activeAccountId

        // Mark account as failed
        setFailedAccountIds((prev) => {
            if (prev.has(accountId)) return prev
            const next = new Set(prev)
            next.add(accountId)
            return next
        })

        // Clear any existing timer for this account
        const cooldownTimers = cooldownTimersRef.current
        const existingTimer = cooldownTimers.get(accountId)
        if (existingTimer) {
            clearTimeout(existingTimer)
        }

        // Set cooldown timer: after ACCOUNT_FETCH_FAILURE_COOLDOWN_MS, clear failed status
        const timeoutId = setTimeout(() => {
            setFailedAccountIds((current) => {
                if (!current.has(accountId)) return current
                const cleared = new Set(current)
                cleared.delete(accountId)
                return cleared
            })
        }, ACCOUNT_FETCH_FAILURE_COOLDOWN_MS)

        cooldownTimers.set(accountId, timeoutId)

        void switchAccount(null)

        // Cleanup: clear timer only if it's still the one we set (not replaced by a newer timer)
        // This prevents premature cleanup when switchAccount() changes isAccountSwitching/activeAccountId
        return () => {
            clearTimeout(timeoutId)
            if (cooldownTimers.get(accountId) === timeoutId) {
                cooldownTimers.delete(accountId)
            }
        }
    }, [userError, activeAccountId, switchAccount, isAccountSwitching])

    // Effect 1: Sync accounts from API to store
    useEffect(() => {
        if (!userAccounts) return
        setAccounts(userAccounts, user?.accountId ?? null)
    }, [userAccounts, setAccounts, user?.accountId])

    // Effect 2: Switch account when resolution changes
    // Kept separate so account syncing doesn't trigger unnecessary switches
    useEffect(() => {
        if (isAccountSwitching) return
        if (shouldDeferUntilPrimaryAccountSettles) return

        if (nextActive !== activeAccountId) {
            // Correcting to an account resolved from data already in hand (e.g. a stale
            // persisted activeAccountId) — nothing changed server-side, so skip the
            // metadata refresh a genuine user-initiated switch performs.
            void switchAccount(nextActive, { refreshMetadata: false })
        }
    }, [activeAccountId, isAccountSwitching, shouldDeferUntilPrimaryAccountSettles, nextActive, switchAccount])

    useEffect(() => {
        if (shouldFinishAccountSwitch) {
            finishAccountSwitch()
            setIsInGracePeriod(true)
            const timeoutId = setTimeout(() => {
                setIsInGracePeriod(false)
            }, ACCOUNT_SWITCH_GRACE_PERIOD_MS)

            return () => clearTimeout(timeoutId)
        }
    }, [shouldFinishAccountSwitch, finishAccountSwitch])

    useEffect(() => {
        if (!user || !activeAccountId) return

        analytics.identify(user.userId, {
            accountId: activeAccountId,
            email: user.emailId,
            firstName: user.firstName ?? undefined,
            lastName: user.lastName ?? undefined,
            utm: getStoredUTMParameters(),
            ...(installedPwa ? { pwa: true } : {}),
        })
    }, [user, activeAccountId, installedPwa])

    return <>{children}</>
}
